import axios from 'axios'
import {Close} from "@mui/icons-material";
import {SectionTitle} from "../../../components/FormCommons";
import {Button, CircularProgress, Dialog, TextField} from "@mui/material";
import {useTheme} from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import {useForm, Controller} from "react-hook-form";
import {useEffect, useRef, useState} from "react";
import {Box} from "@mui/system";
import {useTranslation} from "react-i18next";
import {ReservationPriceField} from "./ReservationDialogFields/ReservationPriceField";
import {useSnackbar} from "notistack";
import {type Company} from "../../../contexts/company-context";
import {ReservationSelectField} from "./ReservationDialogFields/ReservationSelectField";
import {useProductApi} from "../../organizer/events/api/useProductApi";
import {BookingStatus} from "../../../types/wakacje-types";

type BookingData = {
    product_id: string;
    product_name: string;
    company_id: string;
    company_name: string;
    trip_id: string;
    trip_name: string;
    start_date: string;
    end_date: string;
    total_price: number;
    discount_amount: number;
    payable_price: number;
    advance_payment_price: number;
    original_total_price: number;
    paid_amount?: number;
    status?: BookingStatus;
    comment?: string;
}

interface EditBookingProps {
    open: boolean;
    onClose: () => void;
    bookingData?: any;
}

const EditBooking = ({ open, onClose, bookingData }: EditBookingProps) => {
    const [companies, setCompanies] = useState<Company[]>([]);
    const [products, setProducts] = useState([]);
    const [trips, setTrips] = useState([]);
    const [isProductsLoading, setIsProductsLoading] = useState(false);
    const [isTripsLoading, setIsTripsLoading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);

    const theme = useTheme();
    const md = useMediaQuery(theme.breakpoints.up('md'));
    const { enqueueSnackbar } = useSnackbar();
    const { t } = useTranslation();

    const isInitialMount = useRef(true);

    const formMethods = useForm({
        mode: 'onBlur',
        defaultValues: {
            product_id: bookingData?.product_id ?? '',
            company_id: bookingData?.company_id ?? '',
            trip_id: bookingData?.trip_id ?? '',
            start_date: bookingData?.start_date ?? '',
            end_date: bookingData?.end_date ?? '',
            total_price: bookingData?.total_price ?? 0,
            discount_amount: bookingData?.discount_amount ?? 0,
            payable_price: bookingData?.payable_price ?? 0,
            advance_payment_price: bookingData?.advance_payment_price ?? 0,
            original_total_price: bookingData?.original_total_price ?? 0,
            status: bookingData?.status ?? undefined,
            paid_amount: bookingData?.paid_amount ?? 0,
            comment: bookingData?.comment ?? '',
        }
    });

    const {
        handleSubmit,
        control,
        setValue,
        reset,
        watch
    } = formMethods;

    const { listProducts, listTrips } = useProductApi();

    const companyID = watch("company_id");
    const productID = watch("product_id");

    useEffect(() => {
        const fetchAllCompanies = async () => {
            try {
                const companyRes = await axios.get(`${process.env.REACT_APP_WAKACJE_API_URL}/v1/companies?limit=1000`);
                if (companyRes.status === 200) {
                    setCompanies(companyRes.data.data);
                }
            } catch (error) {
                console.error("Błąd pobierania listy firm:", error);
            }
        };
        fetchAllCompanies();
    }, []);

    useEffect(() => {
        if (bookingData) {
            reset({
                product_id: bookingData.product_id ?? '',
                company_id: bookingData.company_id ?? '',
                trip_id: bookingData.trip_id ?? '',
                start_date: bookingData.start_date ?? '',
                end_date: bookingData.end_date ?? '',
                total_price: bookingData.total_price ?? 0,
                discount_amount: bookingData.discount_amount ?? 0,
                payable_price: bookingData.payable_price ?? 0,
                advance_payment_price: bookingData.advance_payment_price ?? 0,
                original_total_price: bookingData.original_total_price ?? 0,
                paid_amount: bookingData.paid_amount ?? undefined,
                status: bookingData.status ?? 0,
                comment: bookingData.comment ?? '',
            });
            isInitialMount.current = true;
            const timer = setTimeout(() => {
                isInitialMount.current = false;
            }, 500);
            return () => clearTimeout(timer);
        }
    }, [bookingData, reset]);

    useEffect(() => {
        const fetchProducts = async () => {
            if (!companyID) {
                setProducts([]);
                setTrips([]);
                return;
            }
            if (!isInitialMount.current) {
                setValue('product_id', '');
                setValue('trip_id', '');
            }
            setIsProductsLoading(true);
            setTrips([]);
            try {
                const productData = await listProducts({ "company_id": companyID }, {});
                setProducts(productData.data || []);
            } catch (error) {
                console.error("Błąd podczas pobierania produktów:", error);
                setProducts([]);
            } finally {
                setIsProductsLoading(false);
            }
        };
        fetchProducts();
    }, [companyID]);

    useEffect(() => {
        const fetchTrips = async () => {
            if (!productID) {
                setTrips([]);
                return;
            }
            if (!isInitialMount.current) {
                setValue('trip_id', '');
            }
            setIsTripsLoading(true);
            try {
                const tripsResult = await listTrips({ limit: 1000, "product_id": productID });
                setTrips(tripsResult.data || []);
            } catch (error) {
                console.error("Błąd podczas pobierania turnusów:", error);
                setTrips([]);
            } finally {
                setIsTripsLoading(false);
            }
        };
        fetchTrips();
    }, [productID]);

    const handleSave = async (data: BookingData) => {
        const {
            product_id,
            company_id,
            trip_id,
            start_date,
            end_date,
            total_price,
            discount_amount,
            payable_price,
            advance_payment_price,
            original_total_price,
            paid_amount,
            status,
            comment
        } = data;

        const bookID = bookingData?.id;
        if (bookID == null || bookID.length === 0) {
            enqueueSnackbar(t('error', 'Nieprawidłowy identyfikator rezerwacji.'), {
                variant: 'error'
            });
            return;
        }

        const selectedProduct = products.find((p: any) => p.id === product_id);
        const selectedCompany = companies.find((c) => c.id === company_id);
        const selectedTrip = trips.find((t: any) => t.id === trip_id);

        const newData: BookingData = {
            product_id,
            product_name: selectedProduct ? selectedProduct.name : '',
            company_id,
            company_name: selectedCompany ? selectedCompany.name : '',
            trip_id,
            trip_name: selectedTrip ? selectedTrip.name : '',
            start_date: selectedTrip ? selectedTrip.start_date : start_date,
            end_date: selectedTrip ? selectedTrip.end_date : end_date,
            total_price,
            discount_amount,
            payable_price,
            advance_payment_price,
            original_total_price,
            paid_amount,
            status,
            comment
        };

        const url = process.env.REACT_APP_WAKACJE_API_URL + `/bookings/v2.0/booking/${bookID}`;
        await axios.post(url, { ...newData });
    };

    const onFormSubmit = async (data: any) => {
        if (isSaving) return;
        const isValid = await formMethods.trigger();
        if (!isValid) return;

        try {
            setIsSaving(true);
            await handleSave(data);
            enqueueSnackbar(t('ui.trips.success_save', 'Zapisano!'), { variant: 'success' });
            setIsSaving(false);
            onClose();
        } catch (err) {
            setIsSaving(false);
            enqueueSnackbar(
                t('ui.trips.error_save', 'Problem z zapisem ') + (err as any)?.message,
                { variant: 'error' }
            );
        }
    };

    const statusOptions = Object.values(BookingStatus).map((status) => ({
        id: status,
        name: status,
    }));

    return (
        <Dialog
            open={open}
            PaperProps={{ sx: { maxWidth: md ? '564px' : '300px', width: '100%', borderRadius: '8px' } }}
            onClose={onClose}
        >
            <div className='flex flex-col gap-4 pt-6 pb-10 px-10'>
                <div>
                    <div className='flex justify-end cursor-pointer' onClick={onClose}>
                        <Close width={16} />
                    </div>
                    <section>
                        <SectionTitle useBlueBackground={false}>
                            {t("superAdminBookings.edit_reservation", "Edytuj rezerwacje")}
                        </SectionTitle>
                        <form onSubmit={handleSubmit(onFormSubmit)} className='p-2'>
                            <ReservationSelectField
                                label={t("superAdminBookings.select_company", "Wybierz firmę")}
                                name="company_id"
                                control={control as any}
                                data={companies}
                            />

                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                                <Box sx={{ flexGrow: 1 }}>
                                    <ReservationSelectField
                                        label={t("superAdminBookings.select_product", "Wybierz produkt")}
                                        name="product_id"
                                        control={control as any}
                                        data={products}
                                        disabled={isProductsLoading || !companyID}
                                    />
                                </Box>
                                {isProductsLoading && <CircularProgress size={24} />}
                            </Box>

                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                                <Box sx={{ flexGrow: 1 }}>
                                    <ReservationSelectField
                                        label={t("superAdminBookings.select_trip", "Wybierz turnus")}
                                        name="trip_id"
                                        control={control as any}
                                        data={trips}
                                        disabled={isTripsLoading || !productID}
                                    />
                                </Box>
                                {isTripsLoading && <CircularProgress size={24} />}
                            </Box>

                            <ReservationPriceField
                                label={t("superAdminBookings.total_price", "Cena całkowita")}
                                name="total_price"
                                control={control as any}
                                setValue={setValue}
                                price={bookingData?.total_price}
                            />
                            <ReservationPriceField
                                label={t("superAdminBookings.discount_amount", "Kwota rabatu")}
                                name="discount_amount"
                                control={control as any}
                                setValue={setValue}
                                price={bookingData?.discount_amount}
                            />
                            <ReservationPriceField
                                label={t("superAdminBookings.payable_price", "Cena do zapłaty")}
                                name="payable_price"
                                control={control as any}
                                setValue={setValue}
                                price={bookingData?.payable_price}
                            />
                            <ReservationPriceField
                                label={t("superAdminBookings.advance_payment_price", "Cena zaliczki")}
                                name="advance_payment_price"
                                control={control as any}
                                setValue={setValue}
                                price={bookingData?.advance_payment_price}
                            />
                            <ReservationPriceField
                                label={t("superAdminBookings.original_total_price", "Cena oryginalna")}
                                name="original_total_price"
                                control={control as any}
                                setValue={setValue}
                                price={bookingData?.original_total_price}
                            />
                            <ReservationSelectField
                                label="Status"
                                name="status"
                                control={control as any}
                                data={statusOptions}
                            />
                            <ReservationPriceField
                                label="Paid amount (optional)"
                                name="paid_amount"
                                control={control as any}
                                setValue={setValue}
                                price={bookingData?.paid_amount}
                            />

                            <Controller
                                name="comment"
                                control={control}
                                render={({ field }) => (
                                    <TextField
                                        {...field}
                                        label={t("superAdminBookings.comment", "Komentarz")}
                                        variant="outlined"
                                        fullWidth
                                        multiline
                                        rows={4}
                                        margin="normal"
                                    />
                                )}
                            />

                            <div className="flex flex-row mb-4 mt-4">
                                <Button onClick={onClose}>
                                    {t("cancel", "Anuluj")}
                                </Button>
                                <Box flex={1} />
                                <Button
                                    color="primary"
                                    variant="contained"
                                    type="submit"
                                    disabled={isSaving || isProductsLoading || isTripsLoading}
                                >
                                    {isSaving ? (
                                        <CircularProgress size={20} color="inherit" />
                                    ) : (
                                        t("save", "Zapisz")
                                    )}
                                </Button>
                            </div>
                        </form>
                    </section>
                </div>
            </div>
        </Dialog>
    );
};

export default EditBooking;
